import axios from "axios";
import { toast } from "react-hot-toast";
import { FormFieldDefinition, FormSection, FormTemplate } from "../db/schema";
import { createTrayStore } from "../utilities/createTrayStore";
import {
  AI_BLANK_WITH_REFERENCE_BYTES,
  AI_REFERENCE_BYTES,
  AiAuditCancelled,
  AiAuditFailed,
  AiAuditLost,
  AiPageImage,
  AutoFieldsStats,
  aiAuditErrorMessage,
  renderPdfPagesForAi,
  startAiFieldAudit,
  waitForAiAudit,
} from "./pdfService";

// Uploading a form template, then letting the AI find and group its fields.
// A tiny store (not React state) so the upload keeps running, and the tray
// keeps showing it, while the officer moves around the app. Same shape as
// libraryUploadService, which feeds the Files tray.

// Lists of templates listen for this to refresh.
export const FORMS_CHANGED_EVENT = "pnai:forms-changed";

export type FormUploadItem = {
  id: string;
  name: string;
  // "processing": uploaded; creating the template or the AI is reading it.
  status: "uploading" | "processing" | "done" | "error";
  progress: number;
  // What the server is doing, or the result once finished.
  after: string;
  error?: string;
  // Set once the template exists; where the tray's name link goes.
  openPath?: string;
};

const store = createTrayStore<FormUploadItem>(["uploading", "processing"]);
const patch = store.patch;

export const subscribeFormUploads = store.subscribe;
export const getFormUploads = store.get;
export const clearFinishedFormUploads = store.clearFinished;

// Tests only.
export function resetFormUploadsForTests() {
  store.reset();
  controllers.forEach((c) => c.abort());
  controllers.clear();
  blocking.clear();
}

// The PDF bytes (and the page renders for the AI) live in this tab. Once the
// pages are sent the server finishes on its own, so only warn before that.
const blocking = new Set<string>();
const warnBeforeLeaving = (e: BeforeUnloadEvent) => {
  e.preventDefault();
  e.returnValue = "";
  return "";
};
const setBlocking = (id: string, on: boolean) => {
  if (on) blocking.add(id);
  else blocking.delete(id);
  window.removeEventListener("beforeunload", warnBeforeLeaving);
  if (blocking.size) window.addEventListener("beforeunload", warnBeforeLeaving);
};

const controllers = new Map<string, AbortController>();


// The X on an uploading file. The template row is only created after the bytes
// land, so stopping here leaves nothing behind.
export function cancelFormUpload(id: string): void {
  const item = store.get().find((i) => i.id === id);
  if (!item || item.status !== "uploading") return;
  controllers.get(id)?.abort();
  setBlocking(id, false);
  store.remove(id);
}

const extractSections = (fields: FormFieldDefinition[]): FormSection[] => {
  const seen = new Set<string>();
  const out: FormSection[] = [];
  for (const f of fields) {
    if (f.section && !seen.has(f.section)) {
      seen.add(f.section);
      out.push({ name: f.section });
    }
  }
  return out;
};

export interface StartFormUploadParams {
  file: File;
  name: string;
  description?: string;
  scope: string;
  isFillable: boolean;
  fields: FormFieldDefinition[];
  // Optional filled-in copy of the same form. Only its page images go to the AI
  // run; the file itself is never uploaded or stored.
  referenceFile?: File;
  // Overrides for the agency flow; officers use the defaults.
  getUploadUrl?: (fileName: string, fileType: string) => Promise<{ uploadURL: string; key: string; url: string }>;
  createTemplate?: (data: any) => Promise<FormTemplate>;
  templatesApiBase?: string;
  // Where the designer lives for a template id, for the tray's link.
  openPath: (templateId: string) => string;
}

export function startFormUpload(params: StartFormUploadParams): string {
  const id = `form-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  const base = params.templatesApiBase ?? "/api/forms/templates";
  const controller = new AbortController();
  controllers.set(id, controller);
  store.add({ id, name: params.name, status: "uploading", progress: 0, after: "" });
  setBlocking(id, true);
  void run(id, params, base, controller);
  return id;
}

async function run(id: string, p: StartFormUploadParams, base: string, controller: AbortController) {
  let templateId: string | undefined;
  try {
    // 1. Presign and put the PDF in S3.
    let uploadURL: string, key: string, url: string;
    if (p.getUploadUrl) {
      ({ uploadURL, key, url } = await p.getUploadUrl(p.file.name, p.file.type));
    } else {
      const { data } = await axios.post("/api/forms/get-upload-url", { fileName: p.file.name, fileType: p.file.type });
      ({ uploadURL, key, url } = data);
    }
    await axios.put(uploadURL, p.file, {
      headers: { "Content-Type": p.file.type },
      signal: controller.signal,
      onUploadProgress: (e) => patch(id, { progress: e.total ? Math.round((e.loaded * 100) / e.total) : 0 }),
    });
    controllers.delete(id);

    // 2. Create the template record with whatever the PDF itself carries.
    patch(id, { status: "processing", progress: 100, after: "creating template" });
    const templateData = {
      name: p.name,
      description: p.description || null,
      original_pdf_url: url,
      original_pdf_key: key,
      scope: p.scope,
      is_fillable: p.isFillable,
      fields: p.fields,
      field_sections: extractSections(p.fields),
      // The server locks the template (no opening, editing, or filling) until
      // the AI pass below finishes.
      ai_processing: true,
    };
    let template: FormTemplate;
    if (p.createTemplate) template = await p.createTemplate(templateData);
    else template = (await axios.post("/api/forms/templates", templateData)).data;
    templateId = template.id;
    window.dispatchEvent(new Event(FORMS_CHANGED_EVENT));

    // 3. The AI reads the form, groups fields, and places missing ones. The work
    // happens on the server and the browser never gives up on it: it hands the
    // pages over, then just watches for as long as this tab is open. Once the
    // server has the job, the browser never deletes the form (the server removes
    // a form it could not read, and saves a result whenever it finishes).
    let handedOff = false;
    try {
      patch(id, { after: "reading your form" });
      const pages = await renderPdfPagesForAi(
        p.file,
        p.referenceFile ? AI_BLANK_WITH_REFERENCE_BYTES : undefined
      );
      const referencePages = p.referenceFile
        ? await renderPdfPagesForAi(p.referenceFile, AI_REFERENCE_BYTES)
        : undefined;
      patch(id, { after: "finding and grouping fields" });
      await startAiFieldAudit(template.id, pages, base, referencePages);
      handedOff = true;
      setBlocking(id, false); // the server carries on from here
      const done = await waitForAiAudit(
        template.id,
        (d, total) => {
          if (total > 0) patch(id, { after: `finding and grouping fields (${d}/${total})` });
        },
        base
      );
      const stats = done.stats as AutoFieldsStats;
      let result = stats.existing
        ? `Reviewed ${stats.existing} fields and added ${stats.added}`
        : `Placed ${stats.added} fields`;
      const exampleFailed = !!referencePages && stats.reference?.applied === false;
      if (exampleFailed) result += ", but the completed example could not be applied";
      else if (referencePages && stats.reference?.applied) result += ` and refined ${stats.reference.refined ?? 0} from your example`;
      patch(id, { status: "done", after: result.toLowerCase(), openPath: p.openPath(template.id) });
      if (exampleFailed) toast(`${result}. Open the form and use Auto-detect with the example to retry.`, { icon: "⚠️" });
      else toast.success(result);
    } catch (aiError) {
      if (!handedOff) {
        // Failed before the server took the job (the pages could not be
        // rendered or sent), so nothing is working on this form. Remove it, or
        // at least unlock it so it can be opened and fixed by hand.
        console.error("AI field analysis could not start:", aiError);
        const reason = aiAuditErrorMessage(aiError);
        const removed = await axios
          .delete(`${base}/${template.id}`)
          .then(() => true)
          .catch(() => false);
        if (!removed) await axios.patch(`${base}/${template.id}`, { ai_processing: false }).catch(() => {});
        patch(
          id,
          removed
            ? { status: "error", error: `Could not read this form (${reason}), so it was removed. Upload it again to retry.` }
            : {
                status: "error",
                openPath: p.openPath(template.id),
                error: `Uploaded, but the fields could not be read (${reason}). Open it and use Auto-detect fields to retry.`,
              }
        );
        toast.error(removed ? `Could not read the form (${reason}), so it was removed` : `Could not auto-detect fields (${reason})`);
      } else if (aiError instanceof AiAuditLost) {
        // We lost sight of the job, which may well have finished.
        patch(id, { status: "done", after: "still analyzing; check the Forms list", openPath: p.openPath(template.id) });
        toast("Still analyzing this form. It will unlock in the Forms list when ready.", { icon: "⏳" });
      } else {
        // The server reported the failure and has already dealt with the form.
        const removed = aiError instanceof AiAuditFailed && aiError.removed;
        const reason = aiAuditErrorMessage(aiError);
        console.error("AI field analysis failed:", aiError);
        patch(
          id,
          removed
            ? { status: "error", error: `${reason}. Upload it again to retry.` }
            : {
                status: "error",
                openPath: p.openPath(template.id),
                error: `Uploaded, but the fields could not be read (${reason}). Open it and use Auto-detect fields to retry.`,
              }
        );
        toast.error(removed ? reason : `Could not auto-detect fields (${reason})`);
      }
    }
  } catch (error: any) {
    // Stopped by the officer: cancelFormUpload already removed the item.
    if (!controller.signal.aborted) {
      console.error(`Failed to upload form ${p.name}:`, error);
      patch(id, { status: "error", error: error?.response?.data?.error || "Upload failed" });
      toast.error("Failed to upload template");
    }
  } finally {
    controllers.delete(id);
    setBlocking(id, false);
    if (templateId) window.dispatchEvent(new Event(FORMS_CHANGED_EVENT));
  }
}

// ---- Auto-detect on a form that already exists ------------------------------
// The server runs the job and keeps the proposal for the officer to review, so
// this only follows it for the tray. The officer can leave the page (or close
// the tab; the server carries on). The form is locked until it finishes, then
// opens with the suggestions ready to approve.
export function trackFieldAudit(params: {
  templateId: string;
  name: string;
  apiBase: string;
  openPath: string;
  // A completed example was sent, so the run has a second pass.
  hasReference?: boolean;
}): string {
  const id = `audit-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  store.add({ id, name: params.name, status: "processing", progress: 100, after: "finding and grouping fields" });
  void (async () => {
    try {
      // Waits on the server's job. The proposal stays on the server.
      const done = await waitForAiAudit(
        params.templateId,
        (done, total) => {
          if (total > 0) patch(id, { after: `finding and grouping fields (${done}/${total})` });
        },
        params.apiBase,
      );
      const exampleFailed = params.hasReference && done?.stats?.reference?.applied === false;
      patch(id, {
        status: "done",
        after: exampleFailed ? "suggestions ready (the completed example could not be applied)" : "suggestions ready to review",
        openPath: params.openPath,
      });
      if (exampleFailed) toast("The completed example could not be applied. You can try again with Auto-detect.", { icon: "⚠️" });
    } catch (error) {
      // Cancelled from the form's locked page: nothing to report.
      if (error instanceof AiAuditCancelled) {
        store.remove(id);
        return;
      }
      // Lost sight of the job (it may well have finished): not a failure.
      if (error instanceof AiAuditLost) {
        patch(id, { status: "done", after: "still analyzing; check the Forms list", openPath: params.openPath });
        return;
      }
      patch(id, { status: "error", error: `Could not read this form (${aiAuditErrorMessage(error)}). It is unchanged.`, openPath: params.openPath });
      toast.error(`Could not auto-detect fields (${aiAuditErrorMessage(error)})`);
    } finally {
      window.dispatchEvent(new Event(FORMS_CHANGED_EVENT));
    }
  })();
  return id;
}

// Render the pages and hand them to the server to read in the background.
// Resolves once the server has accepted the job (the template is then locked);
// the tray follows it from there.
export async function startFieldAudit(params: {
  templateId: string;
  name: string;
  pdf: ArrayBuffer;
  apiBase: string;
  openPath: string;
  onStep?: (label: string) => void;
  // Optional filled-in copy of the form, used for this run only.
  referenceFile?: File;
}): Promise<void> {
  params.onStep?.("Reading the form...");
  const pages: AiPageImage[] = await renderPdfPagesForAi(
    params.pdf,
    params.referenceFile ? AI_BLANK_WITH_REFERENCE_BYTES : undefined
  );
  const referencePages = params.referenceFile
    ? await renderPdfPagesForAi(params.referenceFile, AI_REFERENCE_BYTES)
    : undefined;
  params.onStep?.("Handing off to the server...");
  await axios.post(`${params.apiBase}/${params.templateId}/auto-fields`, {
    pages,
    ...(referencePages?.length && { referencePages }),
    preview: true,
  });
  window.dispatchEvent(new Event(FORMS_CHANGED_EVENT));
  trackFieldAudit({ ...params, hasReference: !!referencePages?.length });
}
